import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "components/ui/toast";
import { Input } from "components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "components/ui/select";
import {
  Loader2,
  Rocket,
  FileText,
  Users,
  MailCheck,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Sparkles,
  ChevronDown,
  X,
  Search,
  Check,
} from "lucide-react";
import {
  fetchUserTemplates,
  getCustomerGroupsRes,
  identityManagementRes,
  createCampaignsRes,
} from "core";
import { useBalance } from "core/context";
import { PageContainer } from "../ui/page-container";
import LaunchHeader from "./LaunchHeader";

const dummyCreateCampaign = async (body: any) =>
  new Promise((r) => setTimeout(() => r({ ok: true, body }), 1000));

interface GroupOption {
  value: string;
  text: string;
  leadsCount?: number;
}

const MultiGroupSelect: React.FC<{
  options: GroupOption[];
  selected: string[];
  onChange: (vals: string[]) => void;
  error?: boolean;
  placeholder?: string;
}> = ({ options, selected, onChange, error, placeholder = "Select audience groups…" }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 640
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Track sm breakpoint
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setIsMobile(mq.matches);
    const h = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  // ── Desktop: smart-flip fixed dropdown ──────────────────────────────
  const [dropdownRect, setDropdownRect] = useState({
    top: 0, bottom: 0, left: 0, width: 0, maxH: 220, openAbove: false,
  });

  const calcPos = useCallback(() => {
    if (!buttonRef.current || isMobile) return;
    const r = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const openAbove = spaceBelow < 240 && spaceAbove > spaceBelow;
    setDropdownRect({
      top: r.bottom + 6,
      bottom: window.innerHeight - r.top + 6,
      left: r.left,
      width: r.width,
      maxH: Math.max((openAbove ? spaceAbove : spaceBelow) - 16, 120),
      openAbove,
    });
  }, [isMobile]);

  useEffect(() => {
    if (!open || isMobile) return;
    calcPos();
    window.addEventListener("scroll", calcPos, true);
    window.addEventListener("resize", calcPos);
    return () => {
      window.removeEventListener("scroll", calcPos, true);
      window.removeEventListener("resize", calcPos);
    };
  }, [open, isMobile, calcPos]);

  // Desktop: close on outside click
  useEffect(() => {
    if (!open || isMobile) return;
    const h = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, isMobile]);

  // Focus search on open
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), isMobile ? 200 : 50);
    else setSearch("");
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || !isMobile) return;
    document.body.classList.add("sheet-open");
    return () => {
      document.body.classList.remove("sheet-open");
    };
  }, [open, isMobile]);

  // ── Helpers ──────────────────────────────────────────────────────────
  const toggle = (val: string) => {
    onChange(selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val]);
  };

  const removeChip = (val: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((v) => v !== val));
  };

  const filtered = options.filter((o) =>
    o.text.toLowerCase().includes(search.toLowerCase())
  );

  const totalContacts = options
    .filter((o) => selected.includes(o.value))
    .reduce((sum, o) => sum + (o.leadsCount ?? 0), 0);

  // ── Shared list content ──────────────────────────────────────────────
  const listContent = (listMaxH?: number) => (
    <>
      {/* Select all / clear */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-line-subtle dark:border-dark-700 bg-gray-50/50 dark:bg-dark-700/30 flex-shrink-0">
          <span className="text-[11px] font-semibold text-content-subtle uppercase tracking-wide">
            {filtered.length} group{filtered.length !== 1 ? "s" : ""}
          </span>
          <div className="flex gap-3">
            <button type="button" onClick={() => onChange(filtered.map((o) => o.value))}
              className="text-[12px] font-semibold text-violet-600 dark:text-violet-400 hover:underline">
              Select all
            </button>
            {selected.length > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <button type="button" onClick={() => onChange([])}
                  className="text-[12px] font-semibold text-content-subtle hover:text-red-500 dark:hover:text-red-400 transition-colors">
                  Clear
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Options — data-sheet-scroll exempts this from document touchmove lock */}
      <div
        data-sheet-scroll="true"
        className="overflow-y-auto py-1 flex-1 min-h-0"
        style={{
          WebkitOverflowScrolling: "touch" as any,
          overflowY: "auto",
          touchAction: "pan-y",   // explicitly allow vertical panning on iOS
          ...(listMaxH ? { maxHeight: listMaxH } : {}),
        }}
      >
        {filtered.length === 0 ? (
          <p className="text-center text-[13px] text-content-subtle dark:text-gray-500 py-8">No groups found</p>
        ) : (
          filtered.map((opt) => {
            const isChecked = selected.includes(opt.value);
            return (
              <button
                type="button" key={opt.value} onClick={() => toggle(opt.value)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-100
                  ${isChecked ? "bg-violet-50 dark:bg-violet-500/10" : "hover:bg-gray-50 dark:hover:bg-dark-700/50"}`}
              >
                <div
                  className={`rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all
                    ${isChecked ? "bg-violet-600 border-violet-600" : "border-line-strong dark:border-dark-500 bg-surface dark:bg-dark-800"}`}
                  style={{ width: 18, height: 18 }}
                >
                  {isChecked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-ui font-semibold truncate
                    ${isChecked ? "text-violet-700 dark:text-violet-300" : "text-gray-800 dark:text-gray-200"}`}>
                    {opt.text}
                  </p>
                </div>
                {opt.leadsCount != null && (
                  <span className={`flex-shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums
                    ${isChecked
                      ? "bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-500/30"
                      : "bg-gray-100 dark:bg-dark-700 text-content-muted dark:text-gray-400 border border-line dark:border-dark-600"
                    }`}>
                    {opt.leadsCount.toLocaleString()} contacts
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Footer summary */}
      {selected.length > 0 && (
        <div className="border-t border-line-subtle dark:border-dark-700 px-4 py-2.5 flex-shrink-0
          bg-violet-50/60 dark:bg-violet-500/[0.08] flex items-center justify-between">
          <span className="text-[12px] font-semibold text-violet-700 dark:text-violet-400">
            {selected.length} group{selected.length !== 1 ? "s" : ""} selected
          </span>
          <span className="text-[12px] font-bold text-violet-700 dark:text-violet-300">
            {totalContacts.toLocaleString()} contacts
          </span>
        </div>
      )}
    </>
  );

  // ── Trigger chip display ─────────────────────────────────────────────
  const triggerChips = selected.length === 0 ? (
    <span className="self-center truncate text-ui leading-[1.25] font-medium text-content-subtle dark:text-gray-500">{placeholder}</span>
  ) : (
    selected.map((val) => {
      const opt = options.find((o) => o.value === val);
      if (!opt) return null;
      return (
        <span key={val} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-lg
          text-[12px] font-semibold
          bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300
          border border-violet-200 dark:border-violet-500/30 max-w-[180px]">
          <span className="truncate">{opt.text}</span>
          {opt.leadsCount != null && (
            <span className="text-[10px] opacity-70 ml-0.5 tabular-nums">{opt.leadsCount.toLocaleString()}</span>
          )}
          <button type="button" onClick={(e) => removeChip(val, e)}
            className="ml-0.5 rounded hover:bg-violet-200 dark:hover:bg-violet-500/30 p-0.5 transition-colors">
            <X className="w-3 h-3" />
          </button>
        </span>
      );
    })
  );

  return (
    <div ref={containerRef} className="relative">
      {/* ── Trigger ── */}
      <button
        ref={buttonRef} type="button" onClick={() => setOpen((v) => !v)}
        className={`${selected.length > 0 ? "!min-h-11" : "!h-11 !min-h-11"} w-full rounded-xl text-ui leading-[1.25] font-medium text-left
          bg-gray-50 dark:bg-dark-700/80
          border ${error ? "border-red-400 dark:border-red-500"
            : open ? "border-indigo-400 ring-2 ring-indigo-400/15"
              : "border-line dark:border-dark-600"}
          transition-all duration-200 px-3 !py-0 flex items-center gap-2`}
      >
        <div className={`${selected.length > 0 ? "min-h-[28px] flex-wrap" : "h-full min-w-0"} flex-1 flex items-center gap-1.5`}>{triggerChips}</div>
        <div className="flex items-center gap-1.5 self-center flex-shrink-0 ml-1">
          {selected.length > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full
              bg-violet-500/15 text-violet-600 dark:text-violet-400 border border-violet-500/20">
              {selected.length} selected
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-content-subtle transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {/* ══════════════════════════════════════════════════════
          MOBILE (< sm): portal-based slide-up bottom sheet
          Rendered into document.body so <main> is NOT in the
          touch event ancestor chain — iOS/Android scroll works.
          ══════════════════════════════════════════════════════ */}
      {isMobile && createPortal(
        open ? (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/55 z-[9998]"
              style={{ animation: "fadeIn 0.18s ease both" }}
              onClick={() => setOpen(false)}
            />

            <div
              className="fixed bottom-0 left-0 right-0 z-[9999]
                bg-surface dark:bg-dark-800 rounded-t-2xl flex flex-col safe-padding-bottom"
              style={{
                maxHeight: "85dvh",
                animation: "sheetSlideUp 0.26s cubic-bezier(0.32,0.72,0,1) both",
              }}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
                <div className="w-8 h-[3px] rounded-full bg-gray-300 dark:bg-dark-500" />
              </div>

              {/* Header — title + X close only (no redundant Done text) */}
              <div className="flex items-center justify-between px-4 pt-1.5 pb-2 flex-shrink-0">
                <div className="flex-1 min-w-0">
                  <h3 className="text-title-sm font-bold text-content dark:text-white leading-snug">
                    Audience Groups
                  </h3>
                  {selected.length > 0 ? (
                    <p className="text-[11px] text-violet-600 dark:text-violet-400 font-semibold mt-0.5">
                      {selected.length} group{selected.length !== 1 ? "s" : ""}&nbsp;·&nbsp;{totalContacts.toLocaleString()} contacts
                    </p>
                  ) : (
                    <p className="text-[11px] text-content-subtle dark:text-gray-500 mt-0.5">
                      Select one or more groups to target
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="ml-3 flex-shrink-0 w-7 h-7 flex items-center justify-center
                    rounded-full bg-gray-100 dark:bg-dark-600
                    text-content-muted dark:text-gray-400
                    hover:bg-gray-200 dark:hover:bg-dark-500 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Search */}
              <div className="px-4 pb-2 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle" />
                  <input
                    ref={searchRef} value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search groups…"
                    className="w-full pl-8 pr-3 py-2 text-[13px] rounded-xl
                      bg-gray-100 dark:bg-dark-700
                      border border-transparent focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/15
                      text-content dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
                      focus:outline-none transition-all"
                  />
                </div>
              </div>

              {/* List — flex-1, scroll handled natively (portal removes main from ancestry) */}
              <div className="flex-1 flex flex-col overflow-hidden min-h-0 border-t border-line-subtle dark:border-dark-700">
                {listContent(undefined)}
              </div>

              {/* Single bottom CTA — no header Done button duplication */}
              <div className="px-4 pt-2.5 pb-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-full py-3.5 rounded-xl text-[14px] font-bold text-white
                    bg-gradient-to-r from-indigo-600 to-violet-600
                    active:scale-[0.98] shadow-md shadow-indigo-500/20 transition-all duration-150"
                >
                  {selected.length > 0
                    ? `Confirm \u00b7 ${selected.length} group${selected.length !== 1 ? "s" : ""} selected`
                    : "Done"}
                </button>
              </div>
            </div>
          </>
        ) : null,
        document.body
      )}

      {open && !isMobile && (
        <div
          className="bg-surface dark:bg-dark-800
            border border-line dark:border-dark-600
            rounded-xl shadow-xl shadow-black/10 dark:shadow-black/40
            overflow-hidden flex flex-col"
          style={{
            position: "fixed",
            ...(dropdownRect.openAbove
              ? { bottom: dropdownRect.bottom }
              : { top: dropdownRect.top }),
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 9999,
            animation: dropdownRect.openAbove ? undefined : "dropdownFadeIn 0.12s ease both",
          }}
        >
          {/* Search */}
          <div className="p-2 border-b border-line-subtle dark:border-dark-700 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle" />
              <input ref={searchRef} value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search groups…"
                className="w-full pl-8 pr-3 py-2 text-[13px] rounded-lg
                  bg-gray-50 dark:bg-dark-700 border border-line dark:border-dark-600
                  text-content dark:text-gray-100 placeholder-gray-400
                  focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/20 transition-all"
              />
            </div>
          </div>

          {listContent(Math.max(dropdownRect.maxH - 90, 100))}
        </div>
      )}
    </div>
  );
};

interface SheetSelectOption { value: string; text: string; }

const SheetSelect: React.FC<{
  options: SheetSelectOption[];
  value: string;
  onValueChange: (val: string) => void;
  placeholder?: string;
  error?: boolean;
  title: string;
}> = ({ options, value, onValueChange, placeholder = "Select…", error, title }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Tablet + mobile breakpoint: < lg (1024px)
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 1024
  );
  const searchRef = useRef<HTMLInputElement>(null);

  // Track lg breakpoint
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    setIsTabletOrMobile(mq.matches);
    const h = (e: MediaQueryListEvent) => setIsTabletOrMobile(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  // Focus search on open
  useEffect(() => {
    if (open && isTabletOrMobile) setTimeout(() => searchRef.current?.focus(), 200);
    else setSearch("");
  }, [open, isTabletOrMobile]);

  // Scroll lock — same pattern as MultiGroupSelect
  useEffect(() => {
    if (!open || !isTabletOrMobile) return;
    document.body.classList.add("sheet-open");
    return () => {
      document.body.classList.remove("sheet-open");
    };
  }, [open, isTabletOrMobile]);

  const filtered = options.filter((o) =>
    o.text.toLowerCase().includes(search.toLowerCase())
  );
  const selectedOption = options.find((o) => o.value === value);

  // ── Desktop: original Select ─────────────────────────────────────────
  if (!isTabletOrMobile) {
    return (
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className={triggerCls(error)}>
          <SelectValue className="text-ui leading-[1.25] font-medium text-content dark:text-gray-100" placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-[280px]">
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} label={opt.text}>
              {opt.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // ── Mobile / Tablet: portal bottom sheet ─────────────────────────────
  return (
    <div className="relative">
      {/* Trigger — shows checkmark dot when value selected */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`!h-11 !min-h-11 w-full rounded-xl text-ui leading-[1.25] font-medium text-left px-3 !py-0
          bg-gray-50 dark:bg-dark-700/80
          border ${error
            ? "border-red-400 dark:border-red-500"
            : open
              ? "border-indigo-400 ring-2 ring-indigo-400/15"
              : selectedOption
                ? "border-violet-300 dark:border-violet-500/50"
                : "border-line dark:border-dark-600"}
          flex items-center justify-between gap-2 transition-all duration-200`}
      >
        <span className={`truncate ${selectedOption
          ? "text-content dark:text-gray-100 font-semibold"
          : "text-content-subtle dark:text-gray-500 font-normal"}`}>
          {selectedOption?.text || placeholder}
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {selectedOption && (
            <span className="w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform duration-200
            ${selectedOption ? "text-violet-400" : "text-content-subtle"}
            ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {/* Portal sheet — renders into document.body so <main> is NOT in ancestor chain */}
      {createPortal(
        open ? (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/55 z-[9998]"
              style={{ animation: "fadeIn 0.18s ease both" }}
              onClick={() => setOpen(false)}
            />

            <div
              className="fixed bottom-0 left-0 right-0 z-[9999]
                bg-surface dark:bg-dark-800 rounded-t-2xl flex flex-col safe-padding-bottom"
              style={{
                maxHeight: "75dvh",
                animation: "sheetSlideUp 0.26s cubic-bezier(0.32,0.72,0,1) both",
              }}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-2 pb-0.5 flex-shrink-0">
                <div className="w-8 h-[3px] rounded-full bg-gray-300 dark:bg-dark-500" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-4 pt-2 pb-2 flex-shrink-0">
                <div className="flex-1 min-w-0">
                  <h3 className="text-title-sm font-bold text-content dark:text-white leading-snug">{title}</h3>
                  {selectedOption ? (
                    <p className="text-micro text-violet-600 dark:text-violet-400 font-semibold mt-0.5 truncate flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-violet-500 inline-flex items-center justify-center flex-shrink-0">
                        <Check className="w-2 h-2 text-white" strokeWidth={3} />
                      </span>
                      {selectedOption.text}
                    </p>
                  ) : (
                    <p className="text-[11px] text-content-subtle dark:text-gray-500 mt-0.5">
                      Tap an option to select
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="ml-3 flex-shrink-0 w-7 h-7 flex items-center justify-center
                    rounded-full bg-gray-100 dark:bg-dark-600
                    text-content-muted dark:text-gray-400
                    hover:bg-gray-200 dark:hover:bg-dark-500 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Search — only if more than 4 options */}
              {options.length > 4 && (
                <div className="px-4 pb-2 flex-shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle" />
                    <input
                      ref={searchRef}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={`Search ${title.toLowerCase()}…`}
                      className="w-full pl-8 pr-3 py-2 text-[13px] rounded-xl
                        bg-gray-100 dark:bg-dark-700
                        border border-transparent focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/15
                        text-content dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
                        focus:outline-none transition-all"
                    />
                  </div>
                </div>
              )}

              {/* Options list */}
              <div
                data-sheet-scroll="true"
                className="flex-1 overflow-y-auto min-h-0 border-t border-line-subtle dark:border-dark-700"
                style={{
                  WebkitOverflowScrolling: "touch" as any,
                  touchAction: "pan-y",
                }}
              >
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2">
                    <p className="text-[13px] text-content-subtle dark:text-gray-500">No results for "{search}"</p>
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="text-[12px] font-semibold text-indigo-600 dark:text-indigo-400"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  filtered.map((opt) => {
                    const isSelected = opt.value === value;
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => { onValueChange(opt.value); setOpen(false); }}
                        className={`w-full flex items-center gap-3 text-left transition-colors duration-100 relative
                          ${isSelected
                            ? "bg-violet-50 dark:bg-violet-500/10"
                            : "hover:bg-gray-50 dark:hover:bg-dark-700/50"}`}
                        style={{ padding: "12px 16px" }}
                      >
                        {/* Left accent bar for selected */}
                        {isSelected && (
                          <span className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r-full bg-violet-500" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`text-ui font-semibold truncate
                            ${isSelected
                              ? "text-violet-700 dark:text-violet-300"
                              : "text-gray-800 dark:text-gray-200"}`}>
                            {opt.text || <span className="italic text-content-subtle dark:text-gray-500">Unnamed</span>}
                          </p>
                        </div>
                        {isSelected && (
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center shadow-sm">
                            <Check className="w-3 h-3 text-white" strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Bottom cancel bar — always visible for thumb-reach dismissal */}
              <div className="px-4 pt-2 pb-3 flex-shrink-0 border-t border-line-subtle dark:border-dark-700">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-full py-2.5 rounded-xl text-[13px] font-semibold
                    bg-gray-100 dark:bg-dark-700
                    text-content-secondary dark:text-gray-300
                    hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                >
                  {selectedOption ? "Done" : "Cancel"}
                </button>
              </div>
            </div>
          </>
        ) : null,
        document.body
      )}
    </div>
  );
};

interface FieldRowProps {
  step: number;
  label: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  accentFrom: string;
  accentTo: string;
  done: boolean;
  error?: boolean;
  errorText?: string;
  hint?: string;
  children: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({
  step, label, icon, iconBg, iconColor,
  accentFrom, accentTo, done, error, errorText, hint, children,
}) => (
  <>
    {/* ── Mobile: standalone card (< sm) ── */}
    <div className={`sm:hidden relative rounded-2xl border mb-3 transition-all duration-300
      bg-surface dark:bg-dark-800
      ${error
        ? "border-red-300 dark:border-red-800/50 shadow-sm"
        : done
          ? "border-line dark:border-dark-600 shadow-sm"
          : "border-line dark:border-dark-700"
      }`}>

      {/* Left accent strip */}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl bg-gradient-to-b ${accentFrom} ${accentTo}`} />

      {/* Card header */}
      <div className="flex items-center gap-2.5 pl-5 pr-4 pt-3.5 pb-2.5">
        <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-[11px] font-bold transition-all
          ${done
            ? `bg-gradient-to-br ${accentFrom} ${accentTo} text-white shadow-sm`
            : error
              ? "bg-red-50 dark:bg-red-900/20 text-red-500 border border-red-200 dark:border-red-800/40"
              : "bg-gray-100 dark:bg-dark-700 text-content-muted dark:text-gray-400"
          }`}>
          {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : step}
        </div>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <span className={iconColor}>{icon}</span>
        </div>
        <span className="text-ui-lg font-bold text-content dark:text-white flex-1 truncate">{label}</span>
        {done && (
          <span className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full
            bg-gradient-to-r ${accentFrom} ${accentTo} text-white`}>
            Done
          </span>
        )}
        {error && (
          <span className="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full
            bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 border border-red-200 dark:border-red-800/30">
            Required
          </span>
        )}
      </div>

      {/* Input area */}
      <div className="pl-5 pr-4 pb-3.5">
        {children}
        {error && errorText && (
          <p className="flex items-center gap-1.5 text-[12px] text-red-500 dark:text-red-400 font-medium mt-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{errorText}
          </p>
        )}
        {!error && hint && (
          <p className="text-[12px] text-content-subtle dark:text-gray-500 mt-2 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 text-amber-400" />{hint}
          </p>
        )}
      </div>
    </div>

    {/* ── Desktop: stepper (sm+) ── */}
    <div className="hidden sm:flex relative gap-5 group transition-all duration-200">
      <div className="flex flex-col items-center flex-shrink-0 w-10">
        <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center
          border-2 transition-all duration-300 flex-shrink-0
          ${done
            ? `bg-gradient-to-br ${accentFrom} ${accentTo} border-transparent shadow-sm`
            : error
              ? "border-red-400 bg-red-50 dark:bg-red-900/20"
              : "border-line dark:border-dark-600 bg-surface dark:bg-dark-800"
          }`}>
          {done
            ? <CheckCircle2 className="w-5 h-5 text-white" />
            : <span className={`text-[13px] font-bold ${error ? "text-red-500" : "text-content-muted dark:text-gray-400"}`}>{step}</span>
          }
        </div>
        {step < 4 && (
          <div className={`w-[2px] flex-1 mt-1 rounded-full transition-all duration-300
            ${done ? `bg-gradient-to-b ${accentFrom} ${accentTo}` : "bg-gray-200 dark:bg-dark-600"}`} />
        )}
      </div>
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2.5 mb-3">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
            <span className={iconColor}>{icon}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-bold text-content dark:text-white">{label}</span>
            {done && (
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full
                bg-gradient-to-r ${accentFrom} ${accentTo} text-white`}>Done</span>
            )}
            {error && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full
                bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 border border-red-100 dark:border-red-900/40">Required</span>
            )}
          </div>
        </div>
        <div className={`rounded-xl transition-all duration-200 ${error ? "ring-1 ring-red-400/50" : ""}`}>
          {children}
        </div>
        {error && errorText && (
          <p className="flex items-center gap-1.5 text-[12px] text-red-500 dark:text-red-400 font-medium mt-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{errorText}
          </p>
        )}
        {!error && hint && (
          <p className="text-[12px] text-content-subtle dark:text-gray-500 mt-2 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 text-amber-400" />{hint}
          </p>
        )}
      </div>
    </div>
  </>
);

const inputCls = (err?: boolean) =>
  `!h-11 !min-h-11 w-full rounded-xl px-3 !py-0 text-[13px] leading-[1.25] font-medium
   bg-gray-50 dark:bg-dark-700/80
   border ${err ? "border-red-400 dark:border-red-500" : "border-line dark:border-dark-600"}
   dark:text-gray-100 dark:placeholder-gray-500/70
   focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/15
   transition-all`;

const triggerCls = (err?: boolean) =>
  `!h-11 !min-h-11 w-full rounded-xl px-3 !py-0 text-[13px] leading-[1.25] font-medium
   bg-gray-50 dark:bg-dark-700/80
   border ${err ? "border-red-400 dark:border-red-500" : "border-line dark:border-dark-600"}
   dark:text-gray-100
   focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/15
   transition-all`;

/* ─── Main ──────────────────────────────────────────────── */
export default function LaunchTemplate() {
  const [campaignSubject, setCampaignSubject] = useState("");
  const [template, setTemplate] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [mailToUse, setMailToUse] = useState("");
  const [friendlyName, setFriendlyName] = useState("");

  const [templateList, setTemplateList] = useState<{ value: string; text: string }[]>([]);
  const [groupList, setGroupList] = useState<{ value: string; text: string; leadsCount?: number }[]>([]);
  const [mailToUseList, setMailToUseList] = useState<{ value: string; text: string; friendlyName?: string }[]>([]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const { balance, setBalance, isLoading: _balanceLoading, refetchBalance } = useBalance();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const tplRes = await fetchUserTemplates?.();
        setTemplateList(
          tplRes?.items?.map((t: any) => ({ value: String(t.id), text: t.name || `Template #${t.id}` })) ?? []
        );
        const grpRes = await getCustomerGroupsRes?.();
        const leadGroup = grpRes.filter((item: any) => item.leadsCount > 0 && item.status === "ACTIVE");
        setGroupList(leadGroup.map((g: any) => ({ value: String(g.id), text: g.name, leadsCount: g.leadsCount })));
        const idRes = await identityManagementRes?.();
        const idMap = idRes?.data || {};
        const successIdentities = Object.values(idMap)
          .filter((i: any) => i.status === "Success")
          .map((i: any) => ({ value: i.email, text: i.email, friendlyName: i.friendlyName }));
        setMailToUseList(successIdentities);
      } catch (err) {
        toast.error("Failed to load launch data");
      } finally {
        setLoading(false);
      }
    })();
    refetchBalance();
  }, [refetchBalance]);

  useEffect(() => {
    const found = mailToUseList.find((m) => m.value === mailToUse);
    setFriendlyName(found?.friendlyName || "");
  }, [mailToUse, mailToUseList]);

  const isFormValidCheck = (key: string, value: any) =>
    setErrors((prev) => ({ ...prev, [key]: !value }));

  const checkAllValidation = () => {
    const invalid: Record<string, boolean> = {
      campaignSubject: !campaignSubject,
      template: !template,
      selectedGroups: selectedGroups.length === 0,
      mailToUse: !mailToUse,
    };
    setErrors(invalid);
    return Object.values(invalid).every((v) => !v);
  };

  const formatDate = (date: Date) => date.toISOString().split("T")[0];
  const getNextDay = (days: number) => {
    const next = new Date(); next.setDate(next.getDate() + days); return formatDate(next);
  };

  const getCampaignName = () => {
    const tpl = templateList.find((t) => t.value === template);
    const now = new Date();
    const ds = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
    return `${tpl?.text || "Campaign"}_${ds}_${Math.random().toString(36).substring(2, 4)}`;
  };

  const balanceDeduction = () => {
    if (typeof balance === "number") {
      setBalance(Math.max(balance - totalSelectedContacts, 0));
    }
  };

  const handleLaunch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkAllValidation()) return;
    const totalNeeded = totalSelectedContacts;
    if (typeof balance === "number" && totalNeeded > balance) {
      toast.error(`Insufficient balance! Need ${totalNeeded}, you have ${balance}.`); return;
    }
    setSubmitting(true);
    toast.info("Launching Template…");
    try {
      await (createCampaignsRes?.({
        name: getCampaignName(), subject: campaignSubject, tid: template,
        sdate: formatDate(new Date()), edate: getNextDay(1), cgrp: selectedGroups, mailToUse, friendlyName,
      }) ?? dummyCreateCampaign({}));
      balanceDeduction();
      toast.dismiss();
      toast.success(`Template launched to ${selectedGroups.length} group${selectedGroups.length > 1 ? "s" : ""}!`);
      setCampaignSubject(""); setTemplate(""); setSelectedGroups([]); setMailToUse(""); setFriendlyName("");
      setErrors({});
    } catch (err) {
      toast.dismiss(); toast.error("Failed to launch template");
    } finally {
      setSubmitting(false);
    }
  };

  const totalSelectedContacts = groupList
    .filter((g) => selectedGroups.includes(g.value))
    .reduce((sum, g) => sum + (g.leadsCount ?? 0), 0);
  const allFilled = !!(campaignSubject && template && selectedGroups.length > 0 && mailToUse);
  const canAfford = typeof balance === "number" && totalSelectedContacts <= balance;

  return (
    <PageContainer>
      <LaunchHeader />

      <div className="mt-1">
        <div className="sm:hidden mb-3">
          <div className="relative overflow-hidden bg-surface dark:bg-dark-800 rounded-2xl
            border border-line-subtle dark:border-dark-700/70
            shadow-[0_1px_2px_rgb(0_0_0/0.04),0_4px_16px_rgb(0_0_0/0.06)]">
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-500" />
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600
                flex items-center justify-center shadow-sm flex-shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-content dark:text-white">Campaign Setup</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-dark-700 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500"
                      style={{ width: `${([!!campaignSubject, !!template, selectedGroups.length > 0, !!mailToUse].filter(Boolean).length / 4) * 100}%` }}
                    />
                  </div>
                  <span className="text-micro font-semibold text-content-subtle dark:text-gray-500 whitespace-nowrap tabular-nums">
                    {[!!campaignSubject, !!template, selectedGroups.length > 0, !!mailToUse].filter(Boolean).length}/4
                  </span>
                </div>
              </div>
              {allFilled && (
                <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold
                  px-2.5 py-1 rounded-full
                  bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400
                  border border-emerald-100 dark:border-emerald-900/40">
                  <CheckCircle2 className="w-3 h-3" /> Ready
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="
          sm:bg-surface sm:dark:bg-dark-800
          sm:border sm:border-line sm:dark:border-dark-700
          sm:rounded-2xl sm:shadow-md sm:dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)] sm:overflow-hidden">

          {/* Accent bar — desktop only */}
          <div className="hidden sm:block h-[3px] w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-500" />

          {/* Desktop card header */}
          <div className="hidden sm:flex items-center gap-3 px-6 py-4 border-b border-line-subtle dark:border-dark-700">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600
              flex items-center justify-center shadow-sm shadow-indigo-500/30">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-title-sm font-bold text-content dark:text-white leading-tight">Campaign Setup</h2>
              <p className="text-[12px] text-content-muted dark:text-gray-400">Complete each step to prepare your launch</p>
            </div>
            {allFilled && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-semibold
                px-3 py-1 rounded-full
                bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400
                border border-emerald-100 dark:border-emerald-900/40">
                <CheckCircle2 className="w-3.5 h-3.5" /> Ready
              </span>
            )}
          </div>

          <div className="sm:px-6 sm:py-7">
            {loading ? (
              <div className="space-y-3 sm:space-y-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i}>
                    {/* Mobile skeleton card */}
                    <div className="sm:hidden bg-surface dark:bg-dark-800 rounded-2xl border border-line dark:border-dark-700 p-4 animate-pulse mb-3">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-dark-700 flex-shrink-0" />
                        <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-dark-700 flex-shrink-0" />
                        <div className="h-4 flex-1 bg-gray-100 dark:bg-dark-700 rounded-lg" />
                      </div>
                      <div className="h-11 bg-gray-100 dark:bg-dark-700 rounded-xl" />
                    </div>
                    {/* Desktop skeleton */}
                    <div className="hidden sm:flex gap-5">
                      <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-dark-700 animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-2.5">
                        <div className="h-4 w-32 bg-gray-100 dark:bg-dark-700 rounded-lg animate-pulse" />
                        <div className="h-10 bg-gray-100 dark:bg-dark-700 rounded-xl animate-pulse" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <form id="launch-form" onSubmit={handleLaunch}>
                <FieldRow
                  step={1} label="Subject"
                  icon={<FileText className="w-4 h-4" />}
                  iconBg="bg-indigo-50 dark:bg-indigo-900/25" iconColor="text-indigo-600 dark:text-indigo-400"
                  accentFrom="from-indigo-500" accentTo="to-indigo-500"
                  done={!!campaignSubject} error={errors.campaignSubject} errorText="Subject is required"
                >
                  <Input
                    value={campaignSubject}
                    onChange={(e) => { setCampaignSubject(e.target.value); isFormValidCheck("campaignSubject", e.target.value); }}
                    placeholder="e.g. Your exclusive offer inside"
                    className={inputCls(errors.campaignSubject)}
                  />
                </FieldRow>

                <FieldRow
                  step={2} label="Email Template"
                  icon={<FileText className="w-4 h-4" />}
                  iconBg="bg-indigo-50 dark:bg-indigo-900/25" iconColor="text-indigo-600 dark:text-indigo-400"
                  accentFrom="from-indigo-500" accentTo="to-violet-500"
                  done={!!template} error={errors.template} errorText="Please select a template"
                  hint={!loading && templateList.length === 0 ? "No templates found — create one in the Templates section first" : undefined}
                >
                  <SheetSelect
                    title="Email Template"
                    options={templateList}
                    value={template}
                    onValueChange={(val) => { setTemplate(val); isFormValidCheck("template", val); }}
                    placeholder="Select a template…"
                    error={errors.template}
                  />
                </FieldRow>

                <FieldRow
                  step={3} label="Audience Groups"
                  icon={<Users className="w-4 h-4" />}
                  iconBg="bg-violet-50 dark:bg-violet-900/25" iconColor="text-violet-600 dark:text-violet-400"
                  accentFrom="from-violet-500" accentTo="to-purple-500"
                  done={selectedGroups.length > 0} error={errors.selectedGroups} errorText="Please select at least one audience group"
                  hint={!loading && groupList.length === 0 ? "No active groups with leads found" : undefined}
                >
                  <MultiGroupSelect
                    options={groupList}
                    selected={selectedGroups}
                    onChange={(vals) => { setSelectedGroups(vals); setErrors((prev) => ({ ...prev, selectedGroups: vals.length === 0 })); }}
                    error={errors.selectedGroups}
                  />
                </FieldRow>

                <FieldRow
                  step={4} label="Sender Email"
                  icon={<MailCheck className="w-4 h-4" />}
                  iconBg="bg-emerald-50 dark:bg-emerald-900/25" iconColor="text-emerald-600 dark:text-emerald-400"
                  accentFrom="from-emerald-500" accentTo="to-teal-500"
                  done={!!mailToUse} error={errors.mailToUse} errorText="Please select a verified sender"
                  hint={!loading && mailToUseList.length === 0 ? "No verified identities found" : undefined}
                >
                  <SheetSelect
                    title="Sender Email"
                    options={mailToUseList}
                    value={mailToUse}
                    onValueChange={(val) => { setMailToUse(val); isFormValidCheck("mailToUse", val); }}
                    placeholder="Select verified sender…"
                    error={errors.mailToUse}
                  />
                </FieldRow>

                {/* Credit notice */}
                {selectedGroups.length > 0 && totalSelectedContacts > 0 && (
                  <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-[12px] font-semibold mb-4
                    ${canAfford
                      ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/40"
                      : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/40"
                    }`}>
                    {canAfford
                      ? <><CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> {totalSelectedContacts.toLocaleString()} credits across {selectedGroups.length} group{selectedGroups.length > 1 ? "s" : ""} — balance sufficient ({(balance ?? 0).toLocaleString()} available)</>
                      : <><AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> Insufficient credits — need {totalSelectedContacts.toLocaleString()}, you have {(balance ?? 0).toLocaleString()}</>
                    }
                  </div>
                )}

                {/* ── Mobile: full-width in-flow Launch CTA ── */}
                <div className="sm:hidden mt-5">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2.5
                      py-4 rounded-2xl
                      text-[15px] font-bold text-white
                      bg-gradient-to-r from-indigo-600 to-violet-600
                      hover:from-indigo-700 hover:to-violet-700
                      active:scale-[0.98]
                      shadow-lg shadow-indigo-500/30
                      transition-all duration-200
                      disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting
                      ? <><Loader2 className="w-5 h-5 animate-spin" /> Launching…</>
                      : <><Rocket className="w-5 h-5" /> Launch Campaign</>
                    }
                  </button>

                  {allFilled && (
                    <p className="text-center text-[11px] text-emerald-600 dark:text-emerald-400 mt-2.5 font-semibold flex items-center justify-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> All steps complete — ready to launch
                    </p>
                  )}
                </div>

                {/* Desktop submit */}
                <div className="hidden sm:flex items-center justify-between pt-4 border-t border-line-subtle dark:border-dark-700">
                  <p className="text-[12px] text-content-muted dark:text-gray-400">
                    {allFilled
                      ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> All fields complete
                      </span>
                      : <span>Complete all 4 steps to launch</span>
                    }
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl
                      text-ui font-bold text-white whitespace-nowrap
                      bg-gradient-to-r from-indigo-600 to-violet-600
                      hover:from-indigo-700 hover:to-violet-700
                      active:scale-[0.98]
                      shadow-sm shadow-indigo-500/25 hover:shadow-md hover:shadow-indigo-500/30
                      transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-indigo-500/40
                      disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-sm">
                    {submitting
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Launching…</>
                      : <><Rocket className="w-4 h-4" /> Launch Template <ChevronRight className="w-3.5 h-3.5 -ml-0.5 opacity-70" /></>
                    }
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
